import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  Copy,
  Loader2,
  Sparkles,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"
import { INVOCATION_INSTRUCTION_MAX_LENGTH } from "../InvocationInstructionField"

import {
  useGenerateAgentTemplateDraftApi,
  useGetAgentTemplateDefinitionApi,
  useListAgentTemplatesApi,
  type GenerateAgentTemplateDraftRequest,
} from "@/hooks/api"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"
import {
  getUserFacingApiErrorMessage,
  normalizeApiError,
} from "@/lib/apiErrors"

import {
  generatedResponseToTemplateResult,
  getInitialAnswers,
} from "./instantiate"
import { resolveLocalizedText } from "./registry"
import { TemplateFormRenderer } from "./form/TemplateFormRenderer"
import type {
  AgentTemplateCatalogItem,
  AgentTemplateDefinition,
  AgentTemplateFormValue,
  AgentTemplateFormValues,
  AgentTemplateDraft,
  AgentTemplateLocale,
} from "./types"

export interface TemplateCreatePanelProps {
  workspaceId: string
  locale: AgentTemplateLocale
  hasGeneratedDraft?: boolean
  onDirtyChange: (dirty: boolean) => void
  onRequestTransition: (
    transition: () => void,
    reason: TemplateTransitionReason
  ) => void
  onRequestGenerate: (generate: () => void) => void
  onBusyChange: (busy: boolean) => void
  onDraftGenerated: (result: AgentTemplateDraft) => void
  onDraftCleared: () => void
  reviewFormId: string
  renderReviewForm: (draft: AgentTemplateDraft) => ReactNode
  isCreating: boolean
}

interface TemplateSetupSnapshot {
  templateId: string
  values: AgentTemplateFormValues
}

type TemplateCreationStep = "choose" | "configure" | "review"
export type TemplateTransitionReason = "switch-template" | "back-to-setup"

interface AgentTemplateCatalogDiagnostic {
  file?: string
  path?: string
  line?: number
  column?: number
  message: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const getCatalogConfigurationError = (error: unknown) => {
  const data = normalizeApiError(error).data
  if (!isRecord(data) || data.code !== "AGENT_TEMPLATE_CONFIGURATION_ERROR") {
    return undefined
  }
  const diagnostics = Array.isArray(data.diagnostics)
    ? data.diagnostics.filter(
        (diagnostic): diagnostic is AgentTemplateCatalogDiagnostic =>
          isRecord(diagnostic) && typeof diagnostic.message === "string"
      )
    : []
  return { diagnostics }
}

const getSetupFingerprint = (snapshot: TemplateSetupSnapshot) =>
  JSON.stringify(snapshot, (_key, value: unknown) => {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
      return value
    }
    return Object.fromEntries(
      Object.entries(value).sort(([left], [right]) => left.localeCompare(right))
    )
  })

export const TemplateCreatePanel = ({
  workspaceId,
  locale,
  hasGeneratedDraft = false,
  onDirtyChange,
  onRequestTransition,
  onRequestGenerate,
  onBusyChange,
  onDraftGenerated,
  onDraftCleared,
  reviewFormId,
  renderReviewForm,
  isCreating,
}: TemplateCreatePanelProps) => {
  const { t } = useTranslation()
  const [catalog, setCatalog] = useState<AgentTemplateCatalogItem[]>([])
  const [catalogWorkspaceId, setCatalogWorkspaceId] = useState("")
  const [templateId, setTemplateId] = useState("")
  const [template, setTemplate] = useState<AgentTemplateDefinition>()
  const [values, setValues] = useState<AgentTemplateFormValues>({})
  const [creationStep, setCreationStep] =
    useState<TemplateCreationStep>("choose")
  const [generatedDraft, setGeneratedDraft] = useState<AgentTemplateDraft>()
  const [itemMetadata, setItemMetadata] = useState<Record<string, unknown>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false)
  const [pendingItemKeys, setPendingItemKeys] = useState<Set<string>>(
    () => new Set()
  )
  const [generationError, setGenerationError] = useState<string>()
  const [templateChanged, setTemplateChanged] = useState(false)
  const [catalogError, setCatalogError] = useState<string>()
  const [catalogDiagnostics, setCatalogDiagnostics] = useState<
    AgentTemplateCatalogDiagnostic[]
  >([])
  const [isCatalogConfigurationError, setIsCatalogConfigurationError] =
    useState(false)
  const [diagnosticsCopied, setDiagnosticsCopied] = useState(false)
  const [isCatalogLoading, setIsCatalogLoading] = useState(true)
  const [catalogReloadKey, setCatalogReloadKey] = useState(0)
  const [definitionReloadKey, setDefinitionReloadKey] = useState(0)
  const errorSummaryRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const generationRequestIdRef = useRef(0)
  const localeRef = useRef(locale)
  const generationLocaleRef = useRef(locale)
  const generationAbortRef = useRef<AbortController | undefined>(undefined)
  const catalogRequestIdRef = useRef(0)
  const definitionRequestIdRef = useRef(0)
  const definitionAbortRef = useRef<AbortController | undefined>(undefined)
  const initialSetupFingerprintRef = useRef<string | undefined>(undefined)
  const { fetchFn: generateAgentTemplateDraft } =
    useGenerateAgentTemplateDraftApi()
  const { fetchFn: listAgentTemplates } = useListAgentTemplatesApi()
  const { fetchFn: getAgentTemplateDefinition } =
    useGetAgentTemplateDefinitionApi()
  const hasPendingItems = pendingItemKeys.size > 0
  const isTemplateBusy = isGeneratingDraft || hasPendingItems

  const handleItemPendingChange = useCallback(
    (key: string, pending: boolean) => {
      setPendingItemKeys((current) => {
        const next = new Set(current)
        if (pending) next.add(key)
        else next.delete(key)
        return next
      })
    },
    []
  )

  localeRef.current = locale

  useEffect(() => {
    onBusyChange(isTemplateBusy)
  }, [isTemplateBusy, onBusyChange])

  useEffect(
    () => () => {
      onBusyChange(false)
    },
    [onBusyChange]
  )

  useEffect(() => {
    if (generationLocaleRef.current === locale) return
    generationLocaleRef.current = locale
    generationRequestIdRef.current += 1
    generationAbortRef.current?.abort()
    generationAbortRef.current = undefined
    setIsGeneratingDraft(false)
    setGenerationError(undefined)
  }, [locale])

  const cancelGenerationAttempt = () => {
    generationRequestIdRef.current += 1
    generationAbortRef.current?.abort()
    generationAbortRef.current = undefined
    setIsGeneratingDraft(false)
    setGenerationError(undefined)
  }

  useEffect(
    () => () => {
      generationRequestIdRef.current += 1
      generationAbortRef.current?.abort()
      catalogRequestIdRef.current += 1
      definitionRequestIdRef.current += 1
      definitionAbortRef.current?.abort()
    },
    []
  )

  useEffect(() => {
    const requestId = catalogRequestIdRef.current + 1
    catalogRequestIdRef.current = requestId
    setCatalog([])
    setCatalogWorkspaceId("")
    setTemplate(undefined)
    setTemplateId("")
    setCreationStep("choose")
    setGeneratedDraft(undefined)
    setItemMetadata({})
    setPendingItemKeys(new Set())
    setCatalogError(undefined)
    setCatalogDiagnostics([])
    setIsCatalogConfigurationError(false)
    setDiagnosticsCopied(false)
    setIsCatalogLoading(true)
    cancelGenerationAttempt()
    definitionRequestIdRef.current += 1
    definitionAbortRef.current?.abort()

    void listAgentTemplates()
      .then((response) => {
        if (catalogRequestIdRef.current !== requestId) return
        const nextCatalog = (response?.data || []) as AgentTemplateCatalogItem[]
        setCatalog(nextCatalog)
        setCatalogWorkspaceId(workspaceId)
        setTemplateId(nextCatalog[0]?.id || "")
        if (nextCatalog.length === 0) {
          setCatalogError(t("workspace.agent.templates.noTemplates"))
        }
      })
      .catch((error) => {
        if (catalogRequestIdRef.current !== requestId) return
        const configurationError = getCatalogConfigurationError(error)
        if (configurationError) {
          setIsCatalogConfigurationError(true)
          setCatalogDiagnostics(configurationError.diagnostics)
          setCatalogError(
            t("workspace.agent.templates.configurationErrorDescription")
          )
          return
        }
        setCatalogError(
          getUserFacingApiErrorMessage(
            error,
            t,
            t("workspace.agent.templates.noTemplates")
          )
        )
      })
      .finally(() => {
        if (catalogRequestIdRef.current === requestId) {
          setIsCatalogLoading(false)
        }
      })
    // Fetch functions intentionally omitted; request ownership is scoped by workspace.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogReloadKey, workspaceId])

  useEffect(() => {
    const selected = catalog.find((candidate) => candidate.id === templateId)
    if (!selected || catalogWorkspaceId !== workspaceId) return
    definitionAbortRef.current?.abort()
    const controller = new AbortController()
    definitionAbortRef.current = controller
    const requestId = definitionRequestIdRef.current + 1
    definitionRequestIdRef.current = requestId
    setTemplate(undefined)
    setCatalogError(undefined)
    setCatalogDiagnostics([])
    setIsCatalogConfigurationError(false)
    setDiagnosticsCopied(false)
    setIsCatalogLoading(true)

    void getAgentTemplateDefinition({
      urlBuilderParams: {
        templateId: selected.id,
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (definitionRequestIdRef.current !== requestId || !response?.data)
          return
        const nextTemplate = response.data as unknown as AgentTemplateDefinition
        const nextValues = getInitialAnswers(nextTemplate, localeRef.current)
        const nextSetupFingerprint = getSetupFingerprint({
          templateId: nextTemplate.id,
          values: nextValues,
        })
        initialSetupFingerprintRef.current ??= nextSetupFingerprint
        setTemplate(nextTemplate)
        setTemplateChanged(false)
        setValues(nextValues)
        setItemMetadata({})
        setFieldErrors({})
        onDirtyChange(
          nextSetupFingerprint !== initialSetupFingerprintRef.current
        )
      })
      .catch((error) => {
        if (definitionRequestIdRef.current !== requestId) return
        const configurationError = getCatalogConfigurationError(error)
        if (configurationError) {
          setIsCatalogConfigurationError(true)
          setCatalogDiagnostics(configurationError.diagnostics)
          setCatalogError(
            t("workspace.agent.templates.configurationErrorDescription")
          )
          return
        }
        setCatalogError(
          getUserFacingApiErrorMessage(
            error,
            t,
            t("workspace.agent.templates.noTemplates")
          )
        )
      })
      .finally(() => {
        if (definitionRequestIdRef.current === requestId) {
          definitionAbortRef.current = undefined
          setIsCatalogLoading(false)
        }
      })
    // Fetch functions intentionally omitted; request ownership is scoped by template identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    catalog,
    catalogWorkspaceId,
    definitionReloadKey,
    templateId,
    workspaceId,
  ])

  const renderFieldError = (errorCode: string | undefined) => {
    if (!errorCode) return undefined
    if (errorCode === "required") {
      return t("workspace.agent.templates.requiredError")
    }
    if (errorCode === "suggested_question_too_long") {
      return t("workspace.agent.templates.suggestedQuestionTooLong")
    }
    if (errorCode === "domains_required") {
      return t("workspace.agent.templates.webSearch.domainsRequired")
    }
    if (errorCode === "too_many_domains") {
      return t("workspace.agent.templates.webSearch.tooManyDomains")
    }
    if (errorCode === "invalid_domains") {
      return t("workspace.agent.templates.webSearch.invalidWebsite")
    }
    if (errorCode === "invalid_kb_selection") {
      return t("workspace.agent.templates.knowledgeBase.invalidSelection")
    }
    if (errorCode === "invalid_staged_upload") {
      return t(
        "workspace.agent.templates.knowledgeBase.stagedUploadUnavailable"
      )
    }
    if (
      ["invalid_model", "model_not_allowed", "model_unavailable"].includes(
        errorCode
      )
    ) {
      return t("workspace.agent.templates.agentModel.unavailable")
    }
    if (errorCode === "model_missing_capability") {
      return t("workspace.agent.templates.agentModel.missingCapability")
    }
    if (errorCode === "image_model_unavailable") {
      return t("workspace.agent.templates.imageGeneration.modelUnavailable")
    }
    if (errorCode === "custom_rule_required") {
      return t("workspace.agent.templates.imageGeneration.customRuleRequired")
    }
    if (errorCode === "invocation_instruction_too_long") {
      return t("workspace.agent.templates.invocationInstructionTooLong", {
        max: INVOCATION_INSTRUCTION_MAX_LENGTH,
      })
    }
    if (
      [
        "invalid_tool_value",
        "invalid_scope",
        "manual_domains_not_allowed",
        "boundary_not_allowed",
        "invalid_trigger",
        "invalid_style",
        "custom_style_not_allowed",
        "scopes_required",
        "details_not_allowed",
        "exclusions_not_allowed",
        "invalid_language",
        "too_long",
      ].includes(errorCode)
    ) {
      return t("workspace.agent.templates.invalidConfiguration")
    }
    return errorCode
  }

  const renderedErrors = Array.from(
    new Set(Object.values(fieldErrors).map(renderFieldError).filter(Boolean))
  ) as string[]

  useEffect(() => {
    if (renderedErrors.length > 0) errorSummaryRef.current?.focus()
  }, [renderedErrors.length])

  useEffect(() => {
    panelRef.current?.scrollIntoView?.({ block: "start" })
  }, [creationStep])

  const selectTemplate = (nextTemplate: AgentTemplateCatalogItem) => {
    if (nextTemplate.id === templateId) return
    onRequestTransition(() => {
      cancelGenerationAttempt()
      definitionRequestIdRef.current += 1
      definitionAbortRef.current?.abort()
      setTemplate(undefined)
      setTemplateId(nextTemplate.id)
      setGeneratedDraft(undefined)
      setFieldErrors({})
      onDirtyChange(
        getSetupFingerprint({
          templateId: nextTemplate.id,
          values,
        }) !== initialSetupFingerprintRef.current
      )
    }, "switch-template")
  }

  const updateValue = (key: string, value: AgentTemplateFormValue) => {
    cancelGenerationAttempt()
    setGeneratedDraft(undefined)
    const nextValues = { ...values, [key]: value }
    setValues(nextValues)
    onDirtyChange(
      getSetupFingerprint({ templateId, values: nextValues }) !==
        initialSetupFingerprintRef.current
    )
    setFieldErrors((current) => {
      if (!current["values." + key]) return current
      const next = { ...current }
      delete next["values." + key]
      return next
    })
  }

  const updateItemMetadata = (key: string, metadata: unknown) => {
    setItemMetadata((current) => ({ ...current, [key]: metadata }))
  }

  const handleGenerate = async () => {
    if (!template || hasPendingItems) return
    setFieldErrors({})
    generationAbortRef.current?.abort()
    const controller = new AbortController()
    generationAbortRef.current = controller
    const requestId = generationRequestIdRef.current + 1
    generationRequestIdRef.current = requestId
    setIsGeneratingDraft(true)
    setGenerationError(undefined)
    setTemplateChanged(false)

    const generationRequest: GenerateAgentTemplateDraftRequest = {
      workspace_id: workspaceId,
      template_id: template.id,
      template_revision: template.revision,
      locale,
      values,
    }

    try {
      const response = await generateAgentTemplateDraft({
        jsonBody: generationRequest,
        signal: controller.signal,
      })
      if (
        generationRequestIdRef.current !== requestId ||
        localeRef.current !== locale
      )
        return
      if (!response?.data)
        throw new Error("Agent draft generation returned no data")

      const result = generatedResponseToTemplateResult(response.data)
      initialSetupFingerprintRef.current = getSetupFingerprint({
        templateId,
        values,
      })
      setGeneratedDraft(result)
      setCreationStep("review")
      onDirtyChange(true)
      onDraftGenerated(result)
    } catch (error) {
      if (
        generationRequestIdRef.current !== requestId ||
        localeRef.current !== locale
      )
        return
      const responseData = (
        error as {
          data?: {
            data?: {
              code?: string
              field_errors?: Record<string, string>
            }
          }
        }
      ).data?.data
      const fieldErrorData = responseData?.field_errors
      if (fieldErrorData) {
        setFieldErrors(fieldErrorData)
        return
      }
      if (responseData?.code === "TEMPLATE_CHANGED") {
        setTemplateChanged(true)
        setGenerationError(t("workspace.agent.templates.templateChanged"))
        return
      }
      setGenerationError(
        getUserFacingApiErrorMessage(
          error,
          t,
          t("workspace.agent.templates.aiGenerationFailedDescription")
        )
      )
    } finally {
      if (generationRequestIdRef.current === requestId) {
        generationAbortRef.current = undefined
        setIsGeneratingDraft(false)
      }
    }
  }

  const copyCatalogDiagnostics = async () => {
    const text = catalogDiagnostics
      .map((diagnostic) => {
        const location = [
          diagnostic.file,
          diagnostic.path,
          diagnostic.line != null
            ? `line ${diagnostic.line}${diagnostic.column != null ? `, column ${diagnostic.column}` : ""}`
            : undefined,
        ]
          .filter(Boolean)
          .join(" · ")
        return [location, diagnostic.message].filter(Boolean).join("\n")
      })
      .join("\n\n")
    try {
      await navigator.clipboard.writeText(text)
      setDiagnosticsCopied(true)
    } catch {
      setDiagnosticsCopied(false)
    }
  }

  if (!template) {
    return (
      <section className="space-y-4" aria-labelledby="agent-template-title">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {isCatalogLoading && (
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            )}
            <h2 id="agent-template-title" className="text-base font-semibold">
              {t("workspace.agent.templates.title")}
            </h2>
          </div>
          {!catalogError && (
            <p className="text-sm text-muted-foreground">
              {t("workspace.agent.templates.description")}
            </p>
          )}
        </div>
        {catalogError && (
          <div
            role="alert"
            className="space-y-4 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
              <div className="min-w-0 space-y-1">
                <p className="font-medium text-destructive">
                  {isCatalogConfigurationError
                    ? t("workspace.agent.templates.configurationErrorTitle")
                    : catalogError}
                </p>
                {isCatalogConfigurationError && (
                  <p className="text-muted-foreground">{catalogError}</p>
                )}
              </div>
            </div>

            {catalogDiagnostics.length > 0 && (
              <ul className="max-h-64 divide-y overflow-y-auto rounded-md border bg-background">
                {catalogDiagnostics.map((diagnostic, index) => {
                  const location = [
                    diagnostic.file,
                    diagnostic.path,
                    diagnostic.line != null
                      ? t("workspace.agent.templates.diagnosticLocation", {
                          line: diagnostic.line,
                          column: diagnostic.column ?? "-",
                        })
                      : undefined,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                  return (
                    <li key={`${location}-${index}`} className="space-y-1 p-3">
                      {location && (
                        <p className="break-all font-mono text-xs text-muted-foreground">
                          {location}
                        </p>
                      )}
                      <p className="whitespace-pre-wrap break-words font-mono text-xs text-foreground">
                        {diagnostic.message}
                      </p>
                    </li>
                  )
                })}
              </ul>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setCatalogReloadKey((value) => value + 1)}
              >
                {t("workspace.agent.templates.reloadTemplates")}
              </Button>
              {catalogDiagnostics.length > 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => void copyCatalogDiagnostics()}
                >
                  {diagnosticsCopied ? (
                    <Check className="mr-2 size-4" />
                  ) : (
                    <Copy className="mr-2 size-4" />
                  )}
                  {diagnosticsCopied
                    ? t("workspace.agent.templates.diagnosticCopied")
                    : t("workspace.agent.templates.copyDiagnostic")}
                </Button>
              )}
            </div>
          </div>
        )}
      </section>
    )
  }

  const steps: Array<{
    id: TemplateCreationStep
    label: string
  }> = [
    {
      id: "choose",
      label: t("workspace.agent.templates.steps.choose"),
    },
    {
      id: "configure",
      label: t("workspace.agent.templates.steps.configure"),
    },
    {
      id: "review",
      label: t("workspace.agent.templates.steps.review"),
    },
  ]
  const currentStepIndex = steps.findIndex(({ id }) => id === creationStep)
  const templateTitle = resolveLocalizedText(template.title, locale)

  return (
    <section
      ref={panelRef}
      className="mx-auto w-full max-w-3xl space-y-8 py-2"
      aria-labelledby="agent-template-title"
    >
      <CopilotHiddenField
        fieldKey="agentTemplate.summary"
        value={JSON.stringify({
          id: template.id,
          revision: template.revision,
          title: resolveLocalizedText(template.title, locale),
          hasGeneratedDraft,
        })}
      />
      <div className="space-y-6">
        <div className="space-y-2 text-center">
          <div className="mx-auto flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Sparkles className="size-4" aria-hidden="true" />
          </div>
          <h2 id="agent-template-title" className="text-xl font-semibold">
            {t("workspace.agent.templates.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("workspace.agent.templates.description")}
          </p>
        </div>

        <nav aria-label={t("workspace.agent.templates.stepNavigation")}>
          <ol className="mx-auto flex w-max max-w-full items-center overflow-x-auto pb-1 text-xs sm:text-sm">
            {steps.map((step, index) => {
              const isCurrent = index === currentStepIndex
              const isComplete = index < currentStepIndex
              return (
                <li
                  key={step.id}
                  className="flex shrink-0 items-center"
                  aria-current={isCurrent ? "step" : undefined}
                >
                  <div
                    className={cn(
                      "flex items-center gap-2 rounded-md px-2.5 py-1.5",
                      isCurrent
                        ? "bg-muted text-foreground"
                        : isComplete
                          ? "text-foreground"
                          : "text-muted-foreground"
                    )}
                  >
                    <span
                      className={cn(
                        "flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold",
                        isCurrent || isComplete
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background"
                      )}
                      aria-hidden="true"
                    >
                      {isComplete ? <Check className="size-3" /> : index + 1}
                    </span>
                    <span
                      className={cn(
                        "whitespace-nowrap",
                        isCurrent ? "font-semibold" : "font-medium"
                      )}
                    >
                      {step.label}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <ChevronRight
                      className="mx-0.5 size-4 text-muted-foreground/60"
                      aria-hidden="true"
                    />
                  )}
                </li>
              )
            })}
          </ol>
        </nav>
      </div>

      {creationStep === "choose" && (
        <div className="space-y-6">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">
              {t("workspace.agent.templates.chooseTitle")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("workspace.agent.templates.chooseDescription")}
            </p>
          </div>

          <div
            role="radiogroup"
            aria-label={t("workspace.agent.templates.chooseTemplate")}
            className="space-y-6"
          >
            {(["general", "language_learning"] as const).map((category) => {
              const categoryTemplates = catalog.filter(
                (candidate) => candidate.category === category
              )
              if (categoryTemplates.length === 0) return null
              return (
                <div key={category} className="space-y-3">
                  <h4 className="text-xs font-medium text-muted-foreground">
                    {t("workspace.agent.templates.categories." + category)}
                  </h4>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {categoryTemplates.map((candidate) => {
                      const isSelected = candidate.id === template.id
                      const candidateTitle = resolveLocalizedText(
                        candidate.title,
                        locale
                      )
                      return (
                        <button
                          key={candidate.id}
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          disabled={isTemplateBusy}
                          onClick={() => selectTemplate(candidate)}
                          className={cn(
                            "group relative min-h-28 rounded-xl border bg-background p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-foreground/30 hover:bg-muted/40"
                          )}
                        >
                          <span className="flex items-start justify-between gap-3">
                            <span className="space-y-1">
                              <span className="block text-sm font-semibold">
                                {candidateTitle}
                              </span>
                              <span className="block text-sm leading-5 text-muted-foreground">
                                {resolveLocalizedText(
                                  candidate.description,
                                  locale
                                )}
                              </span>
                            </span>
                            <span
                              className={cn(
                                "flex size-5 shrink-0 items-center justify-center rounded-full border",
                                isSelected
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-muted-foreground/40"
                              )}
                              aria-hidden="true"
                            >
                              {isSelected && <Check className="size-3" />}
                            </span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>

          <div className="flex justify-end border-t pt-5">
            <Button
              type="button"
              disabled={isTemplateBusy}
              onClick={() => setCreationStep("configure")}
            >
              {t("workspace.agent.templates.continueToSetup")}
              <ArrowRight className="ml-2 size-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}

      {creationStep === "configure" && (
        <div className="space-y-6">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">
              {t("workspace.agent.templates.configureTitle")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("workspace.agent.templates.configureDescription", {
                template: templateTitle,
              })}
            </p>
          </div>

          {renderedErrors.length > 0 && (
            <div
              ref={errorSummaryRef}
              role="alert"
              tabIndex={-1}
              className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive outline-none"
            >
              <p className="font-medium">
                {t("workspace.agent.templates.validationSummary")}
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {renderedErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <TemplateFormRenderer
            workspaceId={workspaceId}
            items={template.form}
            locale={locale}
            values={values}
            itemMetadata={itemMetadata}
            errors={fieldErrors}
            disabled={isTemplateBusy}
            renderError={renderFieldError}
            onItemPendingChange={handleItemPendingChange}
            onItemMetadataChange={updateItemMetadata}
            onChange={updateValue}
          />

          {generationError && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
            >
              <p className="font-medium">
                {t("workspace.agent.templates.aiGenerationFailed")}
              </p>
              <p className="mt-1">{generationError}</p>
            </div>
          )}

          <div className="flex flex-col-reverse justify-between gap-3 border-t pt-5 sm:flex-row">
            <Button
              type="button"
              variant="ghost"
              disabled={isTemplateBusy}
              onClick={() => setCreationStep("choose")}
            >
              <ArrowLeft className="mr-2 size-4" aria-hidden="true" />
              {t("workspace.agent.templates.backToTemplates")}
            </Button>
            <Button
              type="button"
              disabled={isTemplateBusy}
              onClick={
                templateChanged
                  ? () => setDefinitionReloadKey((value) => value + 1)
                  : () => onRequestGenerate(() => void handleGenerate())
              }
            >
              {isGeneratingDraft ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 size-4" />
              )}
              {isGeneratingDraft
                ? t("workspace.agent.templates.generatingDraft")
                : templateChanged
                  ? t("workspace.agent.templates.reloadTemplate")
                  : generationError
                    ? t("workspace.agent.templates.retryAiGeneration")
                    : hasGeneratedDraft
                      ? t("workspace.agent.templates.regenerateDraft")
                      : t("workspace.agent.templates.generateDraftWithAi")}
            </Button>
          </div>
        </div>
      )}

      {creationStep === "review" && generatedDraft && (
        <div className="space-y-6">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">
              {t("workspace.agent.templates.reviewTitle")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("workspace.agent.templates.reviewDescription")}
            </p>
          </div>

          {renderReviewForm(generatedDraft)}

          <p className="text-sm text-muted-foreground">
            {t("workspace.agent.templates.reviewNotCreated")}
          </p>

          <div className="flex flex-col-reverse justify-between gap-3 border-t pt-5 sm:flex-row">
            <Button
              type="button"
              variant="ghost"
              disabled={isCreating}
              onClick={() =>
                onRequestTransition(() => {
                  setGeneratedDraft(undefined)
                  setCreationStep("configure")
                  onDraftCleared()
                }, "back-to-setup")
              }
            >
              <ArrowLeft className="mr-2 size-4" aria-hidden="true" />
              {t("workspace.agent.templates.backToSetup")}
            </Button>
            <Button type="submit" form={reviewFormId} disabled={isCreating}>
              {isCreating && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t("workspace.agent.templates.createAgent")}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
